import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

const OutlookConnected = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const success = searchParams.get("success") !== "false";
  const [countdown, setCountdown] = useState(5);

  const handleClose = useCallback(() => {
    if (success) {
      // Broadcast to all same-origin tabs/windows via localStorage
      localStorage.setItem("outlook_just_connected", String(Date.now()));
    }
    // Try window.close() — works when opened via window.open()
    window.close();
    // Fallback: if window wasn't a popup, navigate back after a tick
    setTimeout(() => navigate(-1), 100);
  }, [navigate, success]);

  useEffect(() => {
    if (!success) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [success]);

  useEffect(() => {
    if (success && countdown === 0) handleClose();
  }, [countdown, success, handleClose]);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f4ff 0%, #e8f5e9 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Segoe UI', sans-serif",
      padding: "20px",
    }}>
      <div style={{
        background: "white",
        borderRadius: "24px",
        padding: "48px 40px",
        maxWidth: "460px",
        width: "100%",
        textAlign: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,0.10)",
        border: "1px solid #e8ecff",
      }}>

        {success ? (
          <>
            {/* Success icon */}
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #0078d4, #00bcf2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 8px 24px rgba(0,120,212,0.35)",
            }}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Microsoft + Outlook branding row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "16px" }}>
              <div style={{
                width: "32px", height: "32px", borderRadius: "8px",
                background: "linear-gradient(135deg, #0078d4, #00bcf2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="18" height="18" viewBox="0 0 32 32" fill="white">
                  <path d="M19 4H5a1 1 0 00-1 1v22a1 1 0 001 1h22a1 1 0 001-1V13L19 4z" />
                  <path d="M19 4v9h9" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
                </svg>
              </div>
              <span style={{ fontSize: "15px", fontWeight: 700, color: "#0078d4" }}>Microsoft Outlook</span>
            </div>

            <h2 style={{ margin: "0 0 10px", fontSize: "22px", fontWeight: 800, color: "#111827" }}>
              Connected Successfully!
            </h2>
            <p style={{ margin: "0 0 28px", fontSize: "14px", color: "#6b7280", lineHeight: 1.6 }}>
              Outlook connected successfully. You can now sync your inbox.
            </p>

            {/* Divider */}
            <div style={{ height: "1px", background: "#f3f4f6", margin: "0 0 24px" }} />

            {/* Features list */}
            {[
              { icon: "📬", text: "Customer email threads synced automatically" },
              { icon: "🔔", text: "New emails appear in your CMS inbox" },
              { icon: "📤", text: "Reply directly from the CMS" },
            ].map(({ icon, text }) => (
              <div key={text} style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px", textAlign: "left" }}>
                <span style={{ fontSize: "18px", flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>{text}</span>
              </div>
            ))}

            <div style={{ height: "1px", background: "#f3f4f6", margin: "24px 0 20px" }} />

            <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#9ca3af" }}>
              This tab will close automatically in <strong style={{ color: "#0078d4" }}>{countdown}s</strong>
            </p>

            <button
              onClick={handleClose}
              style={{
                width: "100%",
                padding: "12px",
                background: "linear-gradient(135deg, #0078d4, #00bcf2)",
                border: "none",
                borderRadius: "12px",
                color: "white",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0,120,212,0.35)",
              }}
            >
              Close this tab
            </button>
          </>
        ) : (
          <>
            {/* Error icon */}
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 8px 24px rgba(239,68,68,0.3)",
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>

            <h2 style={{ margin: "0 0 10px", fontSize: "22px", fontWeight: 800, color: "#111827" }}>
              Connection Failed
            </h2>
            <p style={{ margin: "0 0 28px", fontSize: "14px", color: "#6b7280", lineHeight: 1.6 }}>
              Something went wrong while connecting your Outlook account. Please try again.
            </p>

            <button
              onClick={handleClose}
              style={{
                width: "100%",
                padding: "12px",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                border: "none",
                borderRadius: "12px",
                color: "white",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(239,68,68,0.25)",
              }}
            >
              Close this tab
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OutlookConnected;
